<script lang="ts">
import { type DataFrameDto } from "@/components/datascience/DataFrameWidget.vue";
import CopyableImageWidget from "@/components/ui/CopyableImageWidget.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";

export interface TableReportColumnDto {
  position: number;
  name: string;
  dtype: string;
  value_is_constant: boolean;
  null_count: number;
  null_proportion: number;
  nulls_level: string;
  n_unique: number;
  unique_proportion: number;
  is_duration: boolean;
  duration_unit?: string | null;
  standard_deviation?: number;
  mean?: number;
  inter_quartile_range?: number;
  quantiles?: { [key: string]: number };
  n_low_outliers?: number;
  n_high_outliers?: number;
  plot_names: string[];
  min?: number;
  max?: number;
  [key: string]: unknown;
}

export interface TableReportDto {
  n_rows: number;
  n_columns: number;
  n_constant_columns: number;
  extract_head: DataFrameDto;
  extract_tail: DataFrameDto;
  columns: TableReportColumnDto[];
  top_associations: {
    left_column_name: string;
    left_column_idx: number;
    right_column_name: string;
    right_column_idx: number;
    cramer_v: number;
    pearson_corr: number | null;
  }[];
}

export const HIGH_ASSOCIATION_THRESHOLD = 0.9;
export function tableReportHasHighAssociation(dto: TableReportDto) {
  const highestAssociation = Math.max(...dto.top_associations.map((a) => a.cramer_v));
  return highestAssociation >= HIGH_ASSOCIATION_THRESHOLD;
}

export type TableReportState = "table" | "stats" | "associations";
</script>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import TableReportAssociations from "@/components/datascience/TableReportAssociations.vue";
import TableReportStatistics from "@/components/datascience/TableReportStatistics.vue";
import TableReportTable from "@/components/datascience/TableReportTable.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";

const { spec } = defineProps<{
  spec: TableReportDto;
}>();

const state = ref<TableReportState>("table");
const selectedColumnIndex = ref(-1);
const selectedPlotName = ref("");
const selectedPlotVerboseName = ref("");
const tableReportTable = useTemplateRef<InstanceType<typeof TableReportTable>>("tableReportTable");
const expandableCard = useTemplateRef<InstanceType<typeof ExpandableCard>>("expandableCard");

const selectedColumn = computed(() => spec.columns[selectedColumnIndex.value]);
const columnPlot = computed(() => {
  if (selectedColumnIndex.value !== -1) {
    const firstPlotName = selectedColumn.value?.plot_names[0];
    if (firstPlotName) {
      const svg = selectedColumn.value[firstPlotName] as string;
      return btoa(
        new TextEncoder().encode(svg).reduce((data, byte) => data + String.fromCharCode(byte), "")
      );
    }
  }
  return "";
});

function onColumnIndexSelected(columnIndex: number) {
  selectedColumnIndex.value = columnIndex;

  if (columnIndex !== -1) {
    const selectedColumn = spec.columns[columnIndex];
    const firstPlotName = selectedColumn?.plot_names[0];
    if (firstPlotName) {
      selectedPlotName.value = firstPlotName;
      selectedPlotVerboseName.value = `${selectedColumn?.name} ${firstPlotName.replace("_plot", "").replace(/_/g, " ")}`;
    }
    expandableCard.value?.expand();
  }
}

function onExpanded(isExpanded: boolean) {
  if (!isExpanded) {
    selectedColumnIndex.value = -1;
  } else {
    if (selectedColumnIndex.value === -1) {
      onColumnIndexSelected(0);
    } else {
      onColumnIndexSelected(selectedColumnIndex.value);
    }
  }
  tableReportTable.value?.selectColumnIndex(selectedColumnIndex.value);
}

function selectState(newState: TableReportState) {
  state.value = newState;
}

watch(state, (newState: TableReportState) => {
  if (newState !== "table") {
    expandableCard.value?.collapse();
  }
});

defineExpose({
  selectState,
});
</script>

<template>
  <div class="table-report">
    <ExpandableCard
      ref="expandableCard"
      title="Table report"
      icon="icon-dashboard"
      :is-expandable="state === 'table'"
      @expanded="onExpanded"
    >
      <template #actions>
        <slot name="actions"></slot>
      </template>

      <div class="table-report-content">
        <div class="selector">
          <SimpleButton
            :kind="state === 'table' ? 'primary' : 'ghost'"
            label="Table"
            @click="state = 'table'"
          />

          <SimpleButton
            :kind="state === 'stats' ? 'primary' : 'ghost'"
            label="Stats"
            @click="state = 'stats'"
          />

          <SimpleButton
            :kind="state === 'associations' ? 'primary' : 'ghost'"
            label="Associations"
            @click="state = 'associations'"
            :icon-left="tableReportHasHighAssociation(spec) ? 'icon-warning' : undefined"
          />
        </div>

        <TableReportTable
          v-if="state === 'table'"
          ref="tableReportTable"
          :spec="spec"
          @select-column-index="onColumnIndexSelected"
        />

        <TableReportStatistics v-if="state === 'stats'" :spec="spec" />

        <TableReportAssociations v-if="state === 'associations'" :spec="spec" />
      </div>

      <template #expandableContent>
        <div class="card-content">
          <div v-if="selectedColumnIndex !== -1" class="column-plot">
            <div class="block-title">
              <div class="text-base-medium">
                <i class="title-icon icon icon-bar-chart-2" />
                <DropdownButton
                  v-if="(selectedColumn?.plot_names?.length ?? 0) > 1"
                  :label="selectedPlotVerboseName"
                  icon-left="icon-chevron-down"
                >
                  <DropdownButtonItem
                    v-for="p in selectedColumn?.plot_names"
                    :key="p"
                    :label="`${selectedColumn?.name} ${p.replace('_plot', '').replace(/_/g, ' ')}`"
                    @click="selectedPlotName = p"
                  />
                </DropdownButton>
                <span v-else>{{ selectedPlotVerboseName }}</span>
              </div>
            </div>

            <CopyableImageWidget
              mediaType="image/svg+xml"
              :base64-src="columnPlot"
              :alt="selectedPlotVerboseName"
            />
          </div>
        </div>
      </template>
    </ExpandableCard>
  </div>
</template>

<style scoped>
.table-report {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-16);

  .table-report-content {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);

    .selector {
      display: flex;
      flex-direction: row;
      gap: var(--numbers-8);
    }
  }
}
</style>
